import { flow } from 'lodash';
import Markdown from 'markdown-it';
import texmath from 'markdown-it-texmath';
import katex, { type KatexOptions } from 'katex';

import { type MarkdownRule } from './Markdown.types';

// Словарь: спецсимвол → LaTeX команда
const SPECIAL_CHARS_MAP: Record<string, string> = {
    '·': '\\cdot',
    '×': '\\times',
    '÷': '\\div',
    '±': '\\pm',
    '∓': '\\mp',
    '≠': '\\neq',
    '≤': '\\leq',
    '≥': '\\geq',
    '≈': '\\approx',
    '°': '^\\circ',
};

const latexRule: MarkdownRule = md => {
    const katexWrapper = {
        renderToString: (latex: string, options: KatexOptions) => {
            let processed = latex;

            // Обрабатываем каждый \text{...} блок
            processed = processed.replace(/\\text\{([^}]*)\}/g, (match, content) => {
                // Проверяем, есть ли спецсимволы из словаря
                const hasSpecialChars = Object.keys(SPECIAL_CHARS_MAP).some(char => content.includes(char));

                if (!hasSpecialChars) {
                    return match;
                }

                // Разбиваем по спецсимволам
                let result = content;

                Object.entries(SPECIAL_CHARS_MAP).forEach(([char, latexCmd]) => {
                    if (result.includes(char)) {
                        result = result
                            .split(char)
                            .map((part: string) => `\\text{${part}}`)
                            .join(latexCmd);
                    }
                });

                return result;
            });

            return katex.renderToString(processed, options);
        },
    };

    md.use(texmath, {
        engine: katexWrapper,
        delimiters: 'dollars',
        katexOptions: {
            throwOnError: false,
            strict: false,
            displayMode: false,
            output: 'html',
            trust: false,
        },
    });

    return md;
};

const tableWrapperRule: MarkdownRule = md => {
    const defaultTableOpen =
        md.renderer.rules.table_open || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
    const defaultTableClose =
        md.renderer.rules.table_close || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

    md.renderer.rules.table_open = (tokens, idx, options, env, self) => {
        return '<div class="markdown__table-wrapper">' + defaultTableOpen(tokens, idx, options, env, self);
    };

    md.renderer.rules.table_close = (tokens, idx, options, env, self) => {
        return defaultTableClose(tokens, idx, options, env, self) + '</div>';
    };

    return md;
};

const markdownRules: MarkdownRule[] = [latexRule, tableWrapperRule] as const;

export const createMarkdownRenderer = (): Markdown => {
    const md = Markdown({
        html: true,
        breaks: true,
        typographer: false,
        linkify: true,
    });

    return flow(markdownRules)(md);
};
